import { useState } from "preact/hooks";
import { formatMoney } from "../../lib/format";
import type { PaymentSuccess } from "../../lib/gboModels";
import { downloadReceiptPdf } from "../../lib/gboPdfDownloads";
import { useSiteConfig } from "../../features/tenant/SiteConfigContext";
import { getApiFailureMessage } from "../../store/gboApi";

type DownloadStatus = "idle" | "downloading" | "error";

type DownloadEntry = {
  status: DownloadStatus;
  message: string | null;
};

type Props = {
  success: PaymentSuccess;
  onReset: () => void;
};

export function PaymentSuccessStep({ success, onReset }: Props) {
  const siteConfig = useSiteConfig();
  const [receiptDownload, setReceiptDownload] = useState<DownloadEntry>({
    status: "idle",
    message: null,
  });
  const canMakeAnotherPayment =
    success.accountBalance > 0 &&
    (success.invoice == null || (!success.invoice.closed && !success.invoice.voided));
  const successMessage =
    success.invoice == null
      ? `${formatMoney(success.amountApplied)} applied to account balance.`
      : `${formatMoney(success.amountApplied)} applied to invoice ${success.invoice.code}.`;

  async function downloadReceipt() {
    setReceiptDownload({ status: "downloading", message: null });

    try {
      await downloadReceiptPdf({ id: success.paymentId, siteConfig });
      setReceiptDownload({ status: "idle", message: null });
    } catch (error) {
      setReceiptDownload({
        status: "error",
        message: getApiFailureMessage(error, "Unable to download receipt."),
      });
    }
  }

  return (
    <div class="payment-success-card" role="status">
      <div class="payment-success">
        <strong>Payment successful</strong>
        <span>{successMessage}</span>
      </div>

      <dl class="payment-review">
        <div>
          <dt>Payment amount</dt>
          <dd>{formatMoney(success.paymentAmount)}</dd>
        </div>
        {success.surchargeAmount != null && success.surchargeAmount > 0 && (
          <div>
            <dt>Surcharge</dt>
            <dd>{formatMoney(success.surchargeAmount)}</dd>
          </div>
        )}
        {success.invoice != null && (
          <div>
            <dt>Invoice balance</dt>
            <dd>{formatMoney(success.invoice.balance)}</dd>
          </div>
        )}
        <div>
          <dt>Account balance</dt>
          <dd>{formatMoney(success.accountBalance)}</dd>
        </div>
      </dl>

      {receiptDownload.status === "error" && (
        <p class="payment-error" role="alert">
          {receiptDownload.message}
        </p>
      )}

      <button
        class="primary-action"
        type="button"
        onClick={downloadReceipt}
        disabled={receiptDownload.status === "downloading"}
      >
        {receiptDownload.status === "downloading" ? "Downloading..." : "Download receipt"}
      </button>

      {canMakeAnotherPayment && (
        <button class="secondary-action" type="button" onClick={onReset}>
          Make another payment
        </button>
      )}
    </div>
  );
}
